import { FC, ReactNode } from 'react'
import {
  Box,
  Button,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  ApartmentOutlined,
  ArrowBack,
  AspectRatioOutlined,
  HotelOutlined,
  LayersOutlined,
  PlaceOutlined
} from '@mui/icons-material'
import {
  useLeaseContractFlats,
  useLeaseContractFlatsAndSpaces
} from 'services/contracts/hooks/useLeaseContractsFlats'
import { useActiveLeaseContractId } from 'services/contracts/hooks/useActiveLeaseContractId'
import BackNavigationHeader, { BackNavigationContent } from 'components/BackNavigationHeader'
import { useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'

const StatCard: FC<{ icon: ReactNode; label: string; value: ReactNode }> = ({
  icon,
  label,
  value
}) => {
  const theme = useTheme()

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        backgroundColor: '#fff',
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.06)',
        padding: '24px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px'
      }}>
      <Box
        sx={{
          width: '44px',
          height: '44px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: alpha(theme.r4r.navy, 0.06),
          color: theme.r4r.navy
        }}>
        {icon}
      </Box>
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '0.3px',
          textTransform: 'uppercase',
          color: theme.r4r.grey
        }}>
        {label}
      </Typography>
      <Box>{value}</Box>
    </Box>
  )
}

const AdditionalServicesInformation: FC = () => {
  const { id } = useParams()
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const navigate = useNavigate()
  const { activeLeaseContractId } = useActiveLeaseContractId()
  const { leaseContractsFlats } = useLeaseContractFlats(Number(id))
  const { leaseContractsFlatAccessCodes } = useLeaseContractFlatsAndSpaces(activeLeaseContractId)

  const flat = leaseContractsFlats[0]
  const na = formatMessage({ id: 'common.notAvailable', defaultMessage: 'n/a' })

  const header = isDesktop ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '20px' }}>
      <IconButton
        onClick={() => navigate(`/additional-services/${id}`)}
        sx={{ width: '36px', height: '36px', color: theme.r4r.navy, borderRadius: 0 }}>
        <ArrowBack sx={{ fontSize: '22px' }} />
      </IconButton>
      <Typography
        sx={{ fontSize: '24px', fontWeight: 700, lineHeight: '28.8px', color: theme.r4r.navy }}>
        {formatMessage({
          id: 'additionalServicesInformation.unitInfoTitle',
          defaultMessage: 'Info o lokalu'
        })}
      </Typography>
    </Box>
  ) : null

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isDesktop ? '20px' : '16px' }}>
      <Box
        sx={{
          background: theme.r4r.gradient,
          padding: isDesktop ? '28px 32px' : '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '20px'
        }}>
        <Box
          sx={{
            width: isDesktop ? '56px' : '44px',
            height: isDesktop ? '56px' : '44px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha('#fff', 0.15),
            color: '#fff'
          }}>
          <PlaceOutlined sx={{ fontSize: isDesktop ? '28px' : '22px' }} />
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              color: '#fff',
              opacity: 0.6
            }}>
            {formatMessage({ id: 'additionalServicesInformation.address', defaultMessage: 'Adres' })}
          </Typography>
          <Typography sx={{ fontSize: isDesktop ? '20px' : '16px', fontWeight: 700, color: '#fff', mt: '2px' }}>
            {flat?.flatIdNumber || na}
          </Typography>
        </Box>
      </Box>

      {!!flat && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr 1fr',
              md: 'repeat(4, minmax(0, 1fr))'
            },
            gap: '16px'
          }}>
          <StatCard
            icon={<LayersOutlined sx={{ fontSize: '20px' }} />}
            label={formatMessage({ id: 'additionalServicesInformation.floor', defaultMessage: 'Piętro' })}
            value={
              <Typography sx={{ fontSize: '28px', fontWeight: 800, color: theme.r4r.navy, lineHeight: 1 }}>
                {flat.floor ?? na}
              </Typography>
            }
          />
          <StatCard
            icon={<AspectRatioOutlined sx={{ fontSize: '20px' }} />}
            label={formatMessage({ id: 'additionalServicesInformation.area', defaultMessage: 'Metraż' })}
            value={
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                <Typography sx={{ fontSize: '28px', fontWeight: 800, color: theme.r4r.navy, lineHeight: 1 }}>
                  {flat.area ?? na}
                </Typography>
                {flat.area != null && (
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: theme.r4r.grey }}>
                    m²
                  </Typography>
                )}
              </Box>
            }
          />
          <StatCard
            icon={<HotelOutlined sx={{ fontSize: '20px' }} />}
            label={formatMessage({ id: 'additionalServicesInformation.rooms', defaultMessage: 'Pokoje' })}
            value={
              <Typography sx={{ fontSize: '28px', fontWeight: 800, color: theme.r4r.navy, lineHeight: 1 }}>
                {flat.numberOfRooms ?? na}
              </Typography>
            }
          />
          <StatCard
            icon={<ApartmentOutlined sx={{ fontSize: '20px' }} />}
            label={formatMessage({ id: 'additionalServicesInformation.unit', defaultMessage: 'Lokal' })}
            value={
              <Typography sx={{ fontSize: '18px', fontWeight: 800, color: theme.r4r.navy, lineHeight: 1.2 }}>
                {flat.flatIdNumber || na}
              </Typography>
            }
          />
        </Box>
      )}

      {leaseContractsFlatAccessCodes.map(
        ({
          id: accessId,
          flatIdNumber,
          gateCode,
          investmentContactNumber,
          investmentWifiLogin,
          investmentWifiPassword,
          wicketCode,
          wifiLogin,
          wifiPassword
        }) => {
          const rows = [
            {
              label: formatMessage({
                id: 'additionalServicesInformation.gateCode',
                defaultMessage: 'Kod do klatki schodowej'
              }),
              value: gateCode || na
            },
            {
              label: formatMessage({
                id: 'additionalServicesInformation.wicketCode',
                defaultMessage: 'Kod do furtki'
              }),
              value: wicketCode || na
            },
            {
              label: formatMessage({
                id: 'additionalServicesInformation.wifiLogin',
                defaultMessage: 'Login do Wifi'
              }),
              value: wifiLogin || na
            },
            {
              label: formatMessage({
                id: 'additionalServicesInformation.wifiPassword',
                defaultMessage: 'Hasło do Wifi'
              }),
              value: wifiPassword || na
            },
            {
              label: formatMessage({
                id: 'additionalServicesInformation.investmentWifiLogin',
                defaultMessage: 'Login do Wifi (inwestycja)'
              }),
              value: investmentWifiLogin || na
            },
            {
              label: formatMessage({
                id: 'additionalServicesInformation.investmentWifiPassword',
                defaultMessage: 'Hasło do Wifi (inwestycja)'
              }),
              value: investmentWifiPassword || na
            },
            {
              label: formatMessage({
                id: 'additionalServicesInformation.investmentContactNumber',
                defaultMessage: 'Numer kontaktowy'
              }),
              value: investmentContactNumber || na
            }
          ]

          return (
            <Box
              key={`${flatIdNumber}_${accessId}`}
              sx={{
                backgroundColor: '#fff',
                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.06)',
                padding: isDesktop ? '24px 28px' : '16px 18px'
              }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 700,
                  color: theme.r4r.navy,
                  mb: '16px'
                }}>
                {formatMessage({
                  id: 'additionalServicesInformation.accessData',
                  defaultMessage: 'Dane dostępowe'
                })}
                {leaseContractsFlatAccessCodes.length > 1 && flatIdNumber ? ` · ${flatIdNumber}` : ''}
              </Typography>
              {rows.map((row, index) => (
                <Box
                  key={row.label}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '16px',
                    py: '10px',
                    borderBottom:
                      index === rows.length - 1 ? 'none' : `1px solid ${theme.r4r.greyLight}`
                  }}>
                  <Typography sx={{ fontSize: '13px', color: theme.r4r.grey }}>{row.label}</Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: theme.r4r.navy }}>
                    {row.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )
        }
      )}

      {!!leaseContractsFlats[0]?.energyCertificate && (
        <Button
          href={leaseContractsFlats[0].energyCertificate}
          target='_blank'
          sx={{
            alignSelf: 'flex-start',
            backgroundColor: theme.r4r.navy,
            color: '#fff',
            fontSize: '13px',
            fontWeight: 600,
            borderRadius: 0,
            padding: '11px 20px',
            boxShadow: 'none',
            ':hover': { backgroundColor: theme.r4r.ink, boxShadow: 'none' }
          }}>
          {formatMessage({
            id: 'additionalServicesInformation.energyCertificate',
            defaultMessage: 'Certyfikat energetyczny'
          })}
        </Button>
      )}
    </Box>
  )

  if (isDesktop) {
    return (
      <>
        {header}
        {content}
      </>
    )
  }

  return (
    <>
      <BackNavigationHeader
        label={formatMessage({
          id: 'additionalServicesInformation.unitInfoTitle',
          defaultMessage: 'Info o lokalu'
        })}
      />
      <BackNavigationContent>{content}</BackNavigationContent>
    </>
  )
}

export default AdditionalServicesInformation
